/**
 * Every colour the web client uses. Change a value here and it changes
 * everywhere: the map (CanvasMap.tsx) reads these values directly, and
 * theme.css reads them as CSS variables that installPalette() writes.
 *
 * Values are plain #rrggbb. Transparency is applied where the colour is used
 * (withAlpha() in the map, color-mix() in theme.css).
 */
export const palette = {
  ui: {
    background: "#070b12",
    panel: "#0f1420",
    panelHeader: "#1e2434",
    /** Borders, dividers, scrollbar and table row hover. */
    line: "#7896be",
    shadow: "#000000",
    text: "#e6edf3",
    textDim: "#8b96a8",
    textFaint: "#5a6577",
  },
  accent: {
    cruise: "#00aaff",
    ops: "#ffb648",
  },
  status: {
    ok: "#3ddc84",
    warn: "#ffc107",
    bad: "#ff5252",
  },
  /** Ship dots and course lines on the map, and the state chips in the panels. */
  ship: {
    moving: "#00bbf9",
    stopped: "#f15bb5",
    noRecentSignal: "#ffc107",
    /** 2-hour trails of moving ships (all trails on). The followed ship's track uses the mode accent. */
    trail: "#00bbf9",
    label: "#bed2e6",
    labelSelected: "#d8ecfa",
    halo: "#78b4e6",
  },
  map: {
    sea: "#0e2238",
    seaGlow: "#22446a",
    land: "#1c2e44",
    coastCruise: "#82aad7",
    coastOps: "#d7af73",
    grid: "#96b9e1",
    landDots: "#8caacd",
    /** Port rings and port names. */
    port: "#8c9baf",
  },
  /** Tracking chart (ops mode). The bar colour passed the dataviz validator on the panel colour. */
  chart: {
    bar: "#c47c20",
    barHover: "#dc9128",
    axis: "#96aac8",
  },
} as const;

export type Mode = "cruise" | "ops";

export function accentFor(mode: Mode): string {
  return palette.accent[mode];
}

/** "#rrggbb" + opacity → "rgba(r,g,b,a)" for the canvas. */
export function withAlpha(hex: string, alpha: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

/** The CSS variables theme.css uses, built from the palette. */
export function paletteCss(): string {
  const { ui, accent, status, ship, chart } = palette;
  const vars: Record<string, string> = {
    "--bg": ui.background,
    "--panel": ui.panel,
    "--panel-header": ui.panelHeader,
    "--line": ui.line,
    "--shadow": ui.shadow,
    "--text": ui.text,
    "--text-dim": ui.textDim,
    "--text-faint": ui.textFaint,
    "--accent": accent.cruise,
    "--ok": status.ok,
    "--warn": status.warn,
    "--bad": status.bad,
    "--ship-moving": ship.moving,
    "--ship-stopped": ship.stopped,
    "--ship-no-signal": ship.noRecentSignal,
    "--ship-trail": ship.trail,
    "--chart-axis": chart.axis,
  };
  const root = Object.entries(vars)
    .map(([name, value]) => `${name}: ${value};`)
    .join(" ");
  return `:root { ${root} }\nbody.ops { --accent: ${accent.ops}; }`;
}

/** Adds the palette's CSS variables to the page. Call once before the first render. */
export function installPalette(): void {
  const style = document.createElement("style");
  style.id = "palette";
  style.textContent = paletteCss();
  document.head.prepend(style);
}
